import React, { Fragment, PureComponent } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';

import { ConnectModulesState } from 'src/connect';
import { DEFAULT_EDITOR_KEY } from 'src/connect/file-modules';

import { FileEditorMode } from '../types';

import DiscardChangesDialog from './discard-changes-dialog';
import EditableFileContents from './editable-file-contents';
import { MarkupWrapper } from './file-contents.style';
import FileEditorFooter from './file-editor-footer';
import MarkupFilePreview from './markup-file-preview';
import { RenderProps as MarkupRenderProps } from './markup-file-renderer';
import TextDiff from './text-diff';

type Props = {
  connectEditorModuleKey?: string;
  contents: string;
  fileEditorMode: FileEditorMode;
  height: string;
  isMarkup?: boolean;
  onChangeFileEditorMode: (mode: FileEditorMode) => void;
  onCommitChanges: (contents: string) => void;
  onSelectConnectEditor: (editor?: string) => void;
  path: string;
  repositoryUuid?: string;
  /* eslint react/no-unused-prop-types: "warn" */
  hash: string;
  isColorBlindModeEnabled: boolean;
  isSyntaxHighlightingEnabled: boolean;
};

type State = {
  draft: string;
  isDiscardChangesDialogOpen: boolean;
  prevConnectEditorModuleKey?: string;
  prevIsEditing: boolean;
  supportsConnectEditor: boolean;
};

export default class EditableFile extends PureComponent<Props, State> {
  state = {
    draft: this.props.contents,
    isDiscardChangesDialogOpen: false,
    prevConnectEditorModuleKey: this.props.connectEditorModuleKey,
    prevIsEditing: this.props.fileEditorMode !== 'read-only',
    supportsConnectEditor: true,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    let updates = {};

    const isEditing = props.fileEditorMode !== 'read-only';
    if (isEditing !== state.prevIsEditing) {
      updates = {
        ...updates,
        // Reset the editor draft to the original contents when enabling or disabling the editor
        draft: props.contents,
        // Reset the editor mode to the default when enabling or disabling the editor
        fileEditorMode: 'default',
        prevIsEditing: isEditing,
        // Reset `supportsConnectEditor` to re-check for a matching Connect module
        // when enabling or disabling the editor
        supportsConnectEditor: true,
      };
    }

    if (props.connectEditorModuleKey !== state.prevConnectEditorModuleKey) {
      updates = {
        ...updates,
        prevConnectEditorModuleKey: props.connectEditorModuleKey,
        // Reset `supportsConnectEditor` to re-check for a matching Connect module
        // when the module key changes
        supportsConnectEditor:
          state.supportsConnectEditor ||
          props.connectEditorModuleKey !== state.prevConnectEditorModuleKey,
      };
    }

    return updates;
  }

  handleCancelDiscardChanges = () => {
    this.setState({ isDiscardChangesDialogOpen: false });
  };

  handleConfirmDiscardChanges = () => {
    this.setState({ isDiscardChangesDialogOpen: false });
    this.props.onChangeFileEditorMode('read-only');
  };

  handleConnectMountOrUpdate = (modulesState: ConnectModulesState) => {
    if (!modulesState.loading) {
      const module = modulesState.modules[0];
      const key = module ? module.descriptor.key : DEFAULT_EDITOR_KEY;
      this.props.onSelectConnectEditor(key);
    }

    this.setState({
      supportsConnectEditor:
        modulesState.loading || modulesState.modules.length > 0,
    });
  };

  handleEditCancel = () => {
    if (this.hasUnsavedChanges()) {
      this.setState({ isDiscardChangesDialogOpen: true });
    } else {
      this.props.onChangeFileEditorMode('read-only');
    }
  };

  handleChange = (value: string) => {
    this.setState({ draft: value });
  };

  handleCommitChanges = () => {
    this.props.onCommitChanges(this.state.draft);
  };

  handleFileEditorModeChange = (fileEditorMode: FileEditorMode) => {
    this.props.onChangeFileEditorMode(fileEditorMode);
  };

  hasUnsavedChanges() {
    return this.state.draft !== this.props.contents;
  }

  render() {
    const {
      connectEditorModuleKey,
      contents,
      fileEditorMode,
      path,
      isColorBlindModeEnabled,
      isSyntaxHighlightingEnabled,
    } = this.props;

    const { draft, isDiscardChangesDialogOpen, supportsConnectEditor } =
      this.state;

    const filename = path.split('/').pop();

    return (
      <Fragment>
        <EditableFileContents
          connectEditorModuleKey={connectEditorModuleKey}
          contents={fileEditorMode === 'read-only' ? contents : draft}
          fileEditorMode={fileEditorMode}
          height={this.props.height}
          onChange={this.handleChange}
          onConnectMountOrUpdate={this.handleConnectMountOrUpdate}
          onEditCancel={this.handleEditCancel}
          path={path}
          supportsConnectEditor={
            supportsConnectEditor &&
            connectEditorModuleKey !== DEFAULT_EDITOR_KEY
          }
        />
        {fileEditorMode === 'editor-diff' && (
          <TextDiff
            filename={filename}
            newText={draft}
            oldText={contents}
            isColorBlindModeEnabled={isColorBlindModeEnabled}
            isSyntaxHighlightingEnabled={isSyntaxHighlightingEnabled}
          />
        )}
        {fileEditorMode === 'editor-preview' && (
          <MarkupFilePreview
            content={draft}
            render={({ markupAsRawHtml }: MarkupRenderProps) => (
              <MarkupWrapper
                dangerouslySetInnerHTML={{ __html: markupAsRawHtml }}
              />
            )}
            repositoryUuid={this.props.repositoryUuid}
          />
        )}
        {fileEditorMode !== 'read-only' && (
          <FileEditorFooter
            canPreview={this.props.isMarkup}
            fileEditorMode={fileEditorMode}
            hasUnsavedChanges={this.hasUnsavedChanges()}
            onCancel={this.handleEditCancel}
            onChangeFileEditorMode={this.handleFileEditorModeChange}
            onCommit={this.handleCommitChanges}
          />
        )}
        <ModalTransition>
          {isDiscardChangesDialogOpen && (
            <DiscardChangesDialog
              onCancel={this.handleCancelDiscardChanges}
              onConfirm={this.handleConfirmDiscardChanges}
            />
          )}
        </ModalTransition>
      </Fragment>
    );
  }
}
